// utils/sunTimes.js
// Sunrise/sunset (NOAA) + "isNight" helper with robust timezone day handling.
// No moment import here; works with native Date. You can pass a Date or ISO string.

const ZENITH = 90.833; // official sunrise/sunset
const deg2rad = (d) => (Math.PI / 180) * d;
const rad2deg = (r) => (180 / Math.PI) * r;
const fixAngle = (a) => ((a % 360) + 360) % 360;

function _sunTimeUTC(dateUTC, lat, lng, isSunrise) {
  // dateUTC is a Date at 00:00:00Z for the local civil day
  const day   = dateUTC.getUTCDate();
  const month = dateUTC.getUTCMonth() + 1;
  const year  = dateUTC.getUTCFullYear();

  const N1 = Math.floor((275 * month) / 9);
  const N2 = Math.floor((month + 9) / 12);
  const N3 = 1 + Math.floor((year - 4 * Math.floor(year / 4) + 2) / 3);
  const N  = N1 - N2 * N3 + day - 30;

  const lngHour = lng / 15;
  const t = isSunrise ? N + (6 - lngHour) / 24 : N + (18 - lngHour) / 24;

  const M = 0.9856 * t - 3.289;
  const L = fixAngle(
    M + 1.916 * Math.sin(deg2rad(M)) +
    0.020 * Math.sin(2 * deg2rad(M)) +
    282.634
  );

  let RA = rad2deg(Math.atan(0.91764 * Math.tan(deg2rad(L))));
  RA = fixAngle(RA);
  const Lq  = Math.floor(L / 90) * 90;
  const RAq = Math.floor(RA / 90) * 90;
  RA = (RA + (Lq - RAq)) / 15; // hours

  const sinDec = 0.39782 * Math.sin(deg2rad(L));
  const cosDec = Math.cos(Math.asin(sinDec));

  const cosH = (Math.cos(deg2rad(ZENITH)) - sinDec * Math.sin(deg2rad(lat))) /
               (cosDec * Math.cos(deg2rad(lat)));
  if (cosH > 1 || cosH < -1) return null; // no sunrise or no sunset on this date/lat

  let H = isSunrise ? 360 - rad2deg(Math.acos(cosH)) : rad2deg(Math.acos(cosH));
  H /= 15; // hours

  const T  = H + RA - 0.06571 * t - 6.622;
  const UT = fixAngle((T - lngHour) * 15) / 15; // hours
  return Math.round(UT * 60); // minutes after 00:00 UTC
}

// Get { y, m, d } for a timestamp in a given timezone using Intl (no parsing of localized strings)
function getLocalYMD(dateLike, timeZone) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (!isFinite(d.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);

  const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
  const y = Number(map.year);
  const m = Number(map.month);
  const day = Number(map.day);
  if (!y || !m || !day) return null;
  return { y, m, d: day };
}

// Simple cache per (YYYY-MM-DD|lat|lng)
const _cache = new Map();

function getSunTimesForLocalDay(dateLike, tz = "Europe/London", lat = 51.5074, lng = -0.1278) {
  const ymd = getLocalYMD(dateLike, tz);
  if (!ymd) return { sunriseUTCmin: null, sunsetUTCmin: null, key: null };

  const key = `${ymd.y}-${String(ymd.m).padStart(2, "0")}-${String(ymd.d).padStart(2, "0")}|${lat}|${lng}`;
  if (_cache.has(key)) return _cache.get(key);

  // Build a Date that represents LOCAL midnight, but as a UTC timestamp (00:00Z of that local day)
  // For NOAA calc we only need the UTC civil day corresponding to the local day.
  const localMidnightUTC = new Date(Date.UTC(ymd.y, ymd.m - 1, ymd.d, 0, 0, 0));

  const sunriseUTCmin = _sunTimeUTC(localMidnightUTC, lat, lng, true);
  const sunsetUTCmin  = _sunTimeUTC(localMidnightUTC, lat, lng, false);

  const value = { sunriseUTCmin, sunsetUTCmin, key };
  _cache.set(key, value);
  return value;
}

// Returns true if the timestamp occurs before sunrise or after sunset (nighttime)
export function isNight(tsLike, tz = "Europe/London", lat = 51.5074, lng = -0.1278) {
  const d = tsLike instanceof Date ? tsLike : new Date(tsLike);
  if (!isFinite(d.getTime())) return false; // invalid timestamp -> no icon, no crash

  const { sunriseUTCmin, sunsetUTCmin } = getSunTimesForLocalDay(d, tz, lat, lng);

  // Handle polar edge cases gracefully
  if (sunriseUTCmin == null && sunsetUTCmin == null) {
    return true; // assume night if both missing
  }
  if (sunriseUTCmin == null) return true;  // no sunrise -> always night
  if (sunsetUTCmin == null) return false;  // no sunset -> always day

  const utcMinutesNow = d.getUTCHours() * 60 + d.getUTCMinutes();
  return utcMinutesNow < sunriseUTCmin || utcMinutesNow > sunsetUTCmin;
}
